import { Button } from '@vibTheme/components/_base/Button';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Dispatch, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export type VibDialogProps = DialogProps & {
  open: boolean;
  setIsOpen: Dispatch<React.SetStateAction<boolean>>;
  title: string;
  content?: string | ReactNode;
  cancelBtnText?: string;
  confirmBtnText?: string;
  confirmCallback?: () => void | Promise<void>;
  cancelCallback?: () => void | Promise<void>;
  /**
   * Desabilita as duas ações.
   *
   * Serve para diálogos cujas escolhas são mutuamente exclusivas e disparam
   * requisições incompatíveis: sem isto, o carregamento aparece só no botão de
   * confirmar e dá para clicar no outro enquanto o primeiro está em voo. Opcional,
   * para não mudar o comportamento dos diálogos que já existem.
   */
  disableActions?: boolean;
  /**
   * Desabilita SÓ o confirmar.
   *
   * É o que serve para "o formulário ainda não está gravável": a saída continua aberta. Desabilitar
   * as duas nesse caso prende a pessoa no diálogo — inclusive no estado que pede para ela fechar —,
   * e sobra só o Esc, que não está escrito em lugar nenhum.
   *
   * `disableActions` continua sendo o certo para requisição em voo, onde cancelar também não pode.
   */
  disableConfirm?: boolean;
  /** Só o confirmar, para diálogos em que cancelar faria o mesmo (um "Fechar"). */
  hideCancel?: boolean;
  /** Botões a mais na linha das ações, antes dos dois; não fecham o diálogo. */
  extraActions?: ReactNode;
};

export const VibDialog = (props: VibDialogProps) => {
  const {
    open,
    setIsOpen,
    confirmCallback,
    cancelCallback,
    cancelBtnText,
    confirmBtnText,
    disableActions,
    disableConfirm,
    hideCancel,
    extraActions,
    ...restProps
  } = props;

  const handleClose = () => setIsOpen(false);

  const [isLoading, setIsLoading] = useState(false);
  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await confirmCallback?.();
      handleClose();
    } catch {
      /*
        Confirmação que FALHA não fecha o diálogo — e o `catch` existe para isso não virar rejeição
        solta no clique.
        
        Quem chama já mostrou a mensagem; o que o diálogo tem a fazer é continuar aberto, com o que
        a pessoa preencheu, para ela poder corrigir e tentar de novo. Fechar jogaria fora a escolha
        dela junto com o erro.
      */
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    await cancelCallback?.();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      {...restProps}
      PaperProps={{
        ...restProps.PaperProps,
        className: twMerge('small-scrollbar', restProps.PaperProps?.className),
      }}
    >
      <div className="p-main">
        <DialogTitle id="alert-dialog-title" className="p-0">
          {props.title}
        </DialogTitle>
        <DialogContent className="p-0">
          <div id="alert-dialog-description" className="py-main">
            {props.content}
          </div>
        </DialogContent>
        <DialogActions
          className={`flex ${
            hideCancel ? 'justify-end' : 'justify-between sm:justify-end'
          } p-0`}
        >
          {extraActions}
          {!hideCancel && (
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={disableActions}
            >
              {cancelBtnText || 'Cancelar'}
            </Button>
          )}
          <Button
            id="vib-dialog-confirm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={disableActions || disableConfirm}
          >
            {confirmBtnText || 'Confirmar'}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};
